import { type ReactNode, useEffect, useState } from 'react'
import { useT } from '../lib/i18n'

export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`eyebrow ${className}`}>{children}</div>
}

export function PageHead({
  eyebrow,
  title,
  actions,
}: {
  eyebrow?: string
  title: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
        <h1 className="h1">{title}</h1>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  )
}

export function Card({
  children,
  className = '',
  pad = true,
}: {
  children: ReactNode
  className?: string
  pad?: boolean
}) {
  return <div className={`card ${pad ? 'card-pad' : ''} ${className}`}>{children}</div>
}

export function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'ok' | 'warn' | 'bad'
}) {
  const color =
    tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-sunset' : tone === 'bad' ? 'text-danger' : 'text-mute'
  return (
    <div className="card card-pad">
      <Eyebrow className="mb-3">{label}</Eyebrow>
      <div className="num">{value}</div>
      {sub && <div className={`mono mt-2 text-xs ${color}`}>{sub}</div>}
    </div>
  )
}

const TONE: Record<string, string> = {
  pending: 'badge-warn',
  available: 'badge-ok',
  on_hold: 'badge-bad',
  in_payout: 'badge-info',
  paid: 'badge-dusk',
  reversed: '',
  clawed_back: 'badge-bad',
  approved: 'badge-ok',
  rejected: '',
  blocked: 'badge-bad',
  draft: 'badge-warn',
  queued: 'badge-info',
  sending: 'badge-info',
  sent: 'badge-info',
  failed: 'badge-bad',
  cancelled: '',
  completed: 'badge-ok',
  open: 'badge-bad',
  accepted: 'badge-ok',
  active: 'badge-ok',
  pending_dns: 'badge-warn',
  issuing_cert: 'badge-info',
  error: 'badge-bad',
}

export function Status({ value, kind }: { value: string; kind?: 'enrollment' }) {
  const { t, has } = useT()
  const key = `status.${value}`
  let label = has(key) ? t(key) : value
  if (kind === 'enrollment' && value === 'pending') label = t('enrollmentPending')
  return <span className={`badge ${TONE[value] ?? ''}`}>{label}</span>
}

export function Empty({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode
  title: string
  text?: string
  action?: ReactNode
}) {
  return (
    <div className="card flex flex-col items-center px-6 py-16 text-center">
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-lg border border-hairline text-mute">
        {icon ?? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <rect x="3.5" y="5.5" width="17" height="13" rx="1" />
            <path d="M3.5 9.5h17M8 5.5v13" />
          </svg>
        )}
      </div>
      <h3 className="h2 mb-2">{title}</h3>
      {text && <p className="mb-6 max-w-md text-sm text-mute">{text}</p>}
      {action}
    </div>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </label>
  )
}

export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])
  if (!open) return null
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: click-outside closes the dialog; Escape is handled too
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-16"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`card w-full ${wide ? 'max-w-2xl' : 'max-w-md'}`}
      >
        <div className="flex items-center justify-between border-b border-hairline px-6 py-4">
          <h2 className="h2 text-lg">{title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

export function CopyButton({ value, className = '' }: { value: string; className?: string }) {
  const { t } = useT()
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className={`btn btn-sm ${className}`}
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setDone(true)
          setTimeout(() => setDone(false), 1500)
        })
      }}
    >
      {done ? t('copied') : t('copy')}
    </button>
  )
}

export function CodeBlock({ children }: { children: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-hairline bg-canvas p-2 pl-4">
      <pre className="mono m-0 min-w-0 flex-1 overflow-x-auto py-2 text-xs leading-relaxed text-body">
        {children}
      </pre>
      <CopyButton value={children} className="shrink-0" />
    </div>
  )
}

export function Table({
  head,
  children,
  empty,
}: {
  head: ReactNode[]
  children: ReactNode
  empty?: ReactNode
}) {
  return (
    <div className="card overflow-hidden" style={{ padding: 0 }}>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              {head.map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static header list
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {empty}
    </div>
  )
}

export function ErrorNote({ error }: { error: unknown }) {
  const { t } = useT()
  if (!error) return null
  const code = (error as { code?: string }).code
  return (
    <div role="alert" className="mt-4 rounded-lg border border-danger/40 px-4 py-3 text-sm text-danger">
      {t('error')}
      {code ? <span className="mono ml-2 opacity-80">{code}</span> : null}
    </div>
  )
}

export function Spinner() {
  const { t } = useT()
  return <div className="eyebrow animate-pulse py-10 text-center">{t('loading')}</div>
}

/** Minimal bar chart in SVG (no chart dependency). */
export function Bars({
  data,
  height = 120,
  color = '#ffffff',
}: {
  data: { label: string; value: number }[]
  height?: number
  color?: string
}) {
  const max = Math.max(1, ...data.map((d) => d.value))
  const w = 100 / Math.max(1, data.length)
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className="h-[120px] w-full"
      role="img"
      aria-label="chart"
    >
      {data.map((d, i) => {
        const h = (d.value / max) * (height - 4)
        return (
          <rect
            key={d.label}
            x={i * w + w * 0.18}
            y={height - h}
            width={w * 0.64}
            height={Math.max(h, 0.6)}
            rx={0.6}
            fill={color}
            opacity={d.value ? 0.9 : 0.15}
          >
            <title>{`${d.label}: ${d.value}`}</title>
          </rect>
        )
      })}
    </svg>
  )
}

export function Logo({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true">
        <path
          d="M9 23V9h7.5a4.5 4.5 0 0 1 0 9H13l6 5"
          fill="none"
          stroke="#fff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="23" cy="10" r="2.2" fill="#ff7a17" />
      </svg>
      <span className="text-[18px] tracking-[-0.4px]">RefeGo</span>
    </span>
  )
}
